import type { PayloadAction } from "@reduxjs/toolkit";

import { createAppSlice } from "../app/createAppSlice";
import type { ArtesianDataSeriesType } from "../features/artesian/artesianDataTypes";

export interface SelectedFacet {
  name: string;
  subItems: string[];
}

export interface CustomFilter {
  name: string;
  id: number;
}

export type CustomFilterFacets = Record<string, string[]>;

interface CurveDataState {
  data: ArtesianDataSeriesType[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  selectedFacets: SelectedFacet[] | null | undefined;
  searchInput: string;
  selectedCustomFilter: CustomFilter | null;
  customFilterFacets: CustomFilterFacets | null;
  extractCustomFilterState: boolean;
  filteredTypes: string[];
}

const initialState: CurveDataState = {
  data: [],
  status: "idle",
  error: null,
  selectedFacets: [],
  searchInput: "",
  selectedCustomFilter: null,
  customFilterFacets: null,
  extractCustomFilterState: false,
  filteredTypes: [],
};

export const curveDataSlice = createAppSlice({
  name: "curveData",
  initialState,
  reducers: {
    setSearchInput: (state, action: PayloadAction<string>) => {
      state.searchInput = action.payload;
    },
    setSelectedFacets: (state, action: PayloadAction<SelectedFacet>) => {
      const existingFacet = state.selectedFacets?.find(facet => facet.name === action.payload.name);
      if (existingFacet) {
        existingFacet.subItems = Array.from(new Set([...existingFacet.subItems, ...action.payload.subItems]));
      } else {
        state.selectedFacets?.push(action.payload);
      }
    },
    removeSelectedFacets: (state, action: PayloadAction<SelectedFacet>) => {
      const existingFacet = state.selectedFacets?.find(facet => facet.name === action.payload.name);
      if (existingFacet) {
        existingFacet.subItems = existingFacet.subItems.filter(subItem => !action.payload.subItems.includes(subItem));
        if (existingFacet.subItems.length === 0) {
          state.selectedFacets = state.selectedFacets?.filter(facet => facet.name !== action.payload.name);
        }
      }
    },
    removeWholeFacet: (state, action: PayloadAction<string>) => {
      state.selectedFacets = state.selectedFacets?.filter(facet => facet.name !== action.payload);
    },
    removeAllFacets: state => {
      state.selectedFacets = [];
    },
    setAllFacets: (state, action: PayloadAction<SelectedFacet[]>) => {
      state.selectedFacets = action.payload;
    },
    setSelectedCustomFilter: (state, action: PayloadAction<CustomFilter | null>) => {
      state.selectedCustomFilter = action.payload;
    },
    setCustomFilterFacets: (state, action: PayloadAction<CustomFilterFacets | null>) => {
      state.customFilterFacets = action.payload;
    },
    setExtractCustomFilterState: (state, action: PayloadAction<boolean>) => {
      state.extractCustomFilterState = action.payload;
    },
    setFilteredTypes: (state, action: PayloadAction<string[]>) => {
      state.filteredTypes = action.payload;
    },
  },
  selectors: {
    getSelectedFacets: s => s.selectedFacets,
    getSearchInput: s => s.searchInput,
    getSelectedCustomFilter: s => s.selectedCustomFilter,
    getCustomFilterFacets: s => s.customFilterFacets,
    getExtractCustomFilterState: s => s.extractCustomFilterState,
    getFilteredTypes: s => s.filteredTypes,
  },
});
export const {
  setSelectedFacets,
  removeSelectedFacets,
  removeWholeFacet,
  removeAllFacets,
  setAllFacets,
  setSearchInput,
  setSelectedCustomFilter,
  setCustomFilterFacets,
  setExtractCustomFilterState,
  setFilteredTypes,
} = curveDataSlice.actions;
export const {
  getSelectedFacets,
  getSearchInput,
  getSelectedCustomFilter,
  getCustomFilterFacets,
  getExtractCustomFilterState,
  getFilteredTypes,
} = curveDataSlice.selectors;
